import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { checkAddPermission, checkDeletePermission, checkEditPermission, handleExportFile, handleImportClickFile, handleUpdateAllCheckbox, handleUpdateDataCheckbox } from '../../../Utils/helper';
import { getApi, postApi, putApi, deleteApi } from '../../../Services/apiService';
import { toast } from 'react-toastify';
import Import from '../../../Utils/import';
import { TableFooter } from '../../../Utils/helper';
import DeleteModal from '../../../Utils/deleteModal';

function Category() {
  const { data } = useSelector((state) => state.roleData);
  const [methodType, setMethodType] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [productCategory, setProductCategory] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryCode, setNewCategoryCode] = useState('');
  const [editingProductCategory, setEditingProductCategory] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState('');
  const [editCategoryCode, setEditCategoryCode] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [errors, setErrors] = useState({
    newName: '',
    newCode: '',
    editName: '',
    editCode: ''
  });
  const [touched, setTouched] = useState({
    newName: false,
    newCode: false,
    editName: false,
    editCode: false
  });
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importErrors, setImportErrors] = useState({
    file: '',
    general: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [pageCount, setPageCount] = useState(1);

  const validateInput = (value, fieldName, isCode = false) => {
    if (!value.trim()) {
      return `${fieldName} is required`;
    }
    if (isCode) {
      if (!/^[A-Z0-9]+$/.test(value)) {
        return `${fieldName} should contain only uppercase letters and numbers`;
      }
      if (value.length > 10) {
        return `${fieldName} should be less than 10 characters`;
      }
    } else {
      if (value.length > 50) {
        return `${fieldName} should be less than 50 characters`;
      }
    }
    return '';
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1)
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchProductCategoryData();
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, currentPage, itemsPerPage]);

  const handlePageClick = (selected) => {
    setCurrentPage(selected);
  };

  const fetchProductCategoryData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const resp = await getApi(`/product-category?search=${encodeURIComponent(searchQuery)}&page=${currentPage}&per_page=${itemsPerPage}`);
      if (resp?.success === true) {
        setProductCategory(resp?.data?.productCategories?.data || []);
        setPageCount(resp?.data?.productCategories?.last_page || 1);
        if(resp?.data?.productCategories?.last_page<currentPage){
          setCurrentPage(currentPage-1)
        }
      }
    } catch (error) {
      setError(error.message || 'Failed to fetch category details');
      toast.error('Failed to fetch category details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddCategory = async () => {
    const nameError = validateInput(newCategoryName, 'Category name');
    const codeError = validateInput(newCategoryCode, 'Category code', true);

    setErrors({
      newName: nameError,
      newCode: codeError
    });
    setTouched({
      newName: true,
      newCode: true
    });

    if (nameError || codeError) return;

    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append('name', newCategoryName);
      formData.append('code', newCategoryCode);
      const response = await postApi('/product-category', formData);

      if (response?.success) {
        toast.success(response.msg || 'Product category added successfully');
        setNewCategoryName('');
        setNewCategoryCode('');
        setMethodType('');
        setErrors({ ...errors, newName: '', newCode: '' });
        setTouched({ ...touched, newName: false, newCode: false });
        fetchProductCategoryData();
      } else {
        toast.error(response?.msg || 'Failed to add product category');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.msg || error.message || 'Failed to add product category';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditCategory = async () => {
    const nameError = validateInput(editCategoryName, 'Category name');
    const codeError = validateInput(editCategoryCode, 'Category code', true);

    setErrors({
      editName: nameError,
      editCode: codeError
    });
    setTouched({
      editName: true,
      editCode: true
    });

    if (nameError || codeError || !editingProductCategory) return;

    try {
      setIsLoading(true);
      const payload = {
        name: editCategoryName,
        code: editCategoryCode
      };
      const response = await putApi(`/product-category/${editingProductCategory.id}`, payload);

      if (response?.success) {
        toast.success(response.msg || 'Product category updated successfully');
        setEditCategoryName('');
        setEditCategoryCode('');
        setEditingProductCategory(null);
        setMethodType('');
        setErrors({ ...errors, editName: '', editCode: '' });
        setTouched({ ...touched, editName: false, editCode: false });
        fetchProductCategoryData();
      } else {
        toast.error(response?.msg || 'Failed to update product category');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.msg || error.message || 'Failed to update product category';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const openEditForm = (method) => {
    setEditingProductCategory(method);
    setEditCategoryName(method.name);
    setEditCategoryCode(method.code);
    setMethodType('edit');
    setErrors({ ...errors, editName: '', editCode: '' });
    setTouched({ ...touched, editName: false, editCode: false });
  };

  const handleNewNameChange = (e) => {
    const value = e.target.value;
    setNewCategoryName(value);
    if (touched.newName) {
      setErrors({ ...errors, newName: validateInput(value, 'Category name') });
    }
  };

  const handleNewCodeChange = (e) => {
    const value = e.target.value.toUpperCase();
    setNewCategoryCode(value);
    if (touched.newCode) {
      setErrors({ ...errors, newCode: validateInput(value, 'Category code', true) });
    }
  };

  const handleEditNameChange = (e) => {
    const value = e.target.value;
    setEditCategoryName(value);
    if (touched.editName) {
      setErrors({ ...errors, editName: validateInput(value, 'Category name') });
    }
  };

  const handleEditCodeChange = (e) => {
    const value = e.target.value.toUpperCase();
    setEditCategoryCode(value);
    if (touched.editCode) {
      setErrors({ ...errors, editCode: validateInput(value, 'Category code', true) });
    }
  };

  return (
    <div className="RightSideDiv product-single category">

      {showImportModal && <Import setImportFile={setImportFile} importFile={importFile} searchQuery={searchQuery} fetchData={fetchProductCategoryData} setShowImportModal={setShowImportModal} routeEnd={'/product-category/import'} routeexport={'/product-category/export'} type={'Category'} />
      }
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <DeleteModal
          setShowDeleteModal={setShowDeleteModal}
          todelete={categoryToDelete}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          settoDelete={setCategoryToDelete}
          fetchData={fetchProductCategoryData}
          route={`/product-category/${categoryToDelete?.id}`}
          type={'category'}
        />
      )}

      {/* Add Category Form */}
      {methodType === 'add' && (
        <div className="row">
          <div className="col-sm-12">
            <div className="setting-right position-relative">
              <h3>Add Category</h3>
                         <div className="button-settings">
                <div className="col-sm-12">
                  <div className="d-flex justify-content-end gap-3">
                    <button
                      className="reset"
                      onClick={() => {
                        setNewCategoryName('');
                        setNewCategoryCode('');
                        setErrors({ ...errors, newName: '', newCode: '' });
                        setTouched({ ...touched, newName: false, newCode: false });
                      }}
                    >
                      Reset
                    </button>
                    <button
                      className="save"
                      onClick={handleAddCategory}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
              <img
                src={window.getPublicUrl('/assets/images/close.svg')}
                className="close"
                alt="Close"
                onClick={() => {
                  setMethodType('');
                  setNewCategoryName('');
                  setNewCategoryCode('');
                  setErrors({ ...errors, newName: '', newCode: '' });
                  setTouched({ ...touched, newName: false, newCode: false });
                }}
              />
              <div className="setting-inner">
                <div className="row">
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label>
                        Category<strong>*</strong>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${touched.newName && errors.newName ? 'is-invalid' : ''}`}
                        placeholder="Enter Category"
                        value={newCategoryName}
                        onChange={handleNewNameChange}
                        onBlur={() => {
                          setTouched({ ...touched, newName: true });
                          setErrors({ ...errors, newName: validateInput(newCategoryName, 'Category name') });
                        }}
                        maxLength={50}
                      />
                      {touched.newName && errors.newName && (
                        <div className="invalid-feedback d-block">
                          {errors.newName}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label>
                        Code<strong>*</strong>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${touched.newCode && errors.newCode ? 'is-invalid' : ''}`}
                        placeholder="Enter Code (e.g. 01)"
                        value={newCategoryCode}
                        onChange={handleNewCodeChange}
                        onBlur={() => {
                          setTouched({ ...touched, newCode: true });
                          setErrors({ ...errors, newCode: validateInput(newCategoryCode, 'Category code', true) });
                        }}
                        maxLength={10}
                      />
                      {touched.newCode && errors.newCode && (
                        <div className="invalid-feedback d-block">
                          {errors.newCode}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {/* <div className="button-settings">
                <div className="col-sm-12">
                  <div className="d-flex justify-content-end gap-3">
                    <button
                      className="reset"
                      onClick={() => {
                        setNewCategoryName('');
                        setNewCategoryCode('');
                        setErrors({ ...errors, newName: '', newCode: '' });
                        setTouched({ ...touched, newName: false, newCode: false });
                      }}
                    >
                      Reset
                    </button>
                    <button
                      className="save"
                      onClick={handleAddCategory}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      )}

      {/* Edit Category Form */}
      {methodType === 'edit' && (
        <div className="row">
          <div className="col-sm-12">
            <div className="setting-right position-relative">
              <h3>Edit Category</h3>
                <div className="button-settings">
                <div className="col-sm-12">
                  <div className="d-flex justify-content-end gap-3">
                    <button
                      className="reset"
                      onClick={() => {
                        setEditCategoryName(editingProductCategory.name);
                        setEditCategoryCode(editingProductCategory.code);
                        setErrors({ ...errors, editName: '', editCode: '' });
                        setTouched({ ...touched, editName: false, editCode: false });
                      }}
                    >
                      Reset
                    </button>
                    <button
                      className="save"
                      onClick={handleEditCategory}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
              <img
                src={window.getPublicUrl('/assets/images/close.svg')}
                className="close"
                alt="Close"
                onClick={() => {
                  setMethodType('');
                  setEditCategoryName('');
                  setEditCategoryCode('');
                  setEditingProductCategory(null);
                  setErrors({ ...errors, editName: '', editCode: '' });
                  setTouched({ ...touched, editName: false, editCode: false });
                }}
              />
              <div className="setting-inner">
                <div className="row">
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label>
                        Category<strong>*</strong>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${touched.editName && errors.editName ? 'is-invalid' : ''}`}
                        placeholder="Enter Category"
                        value={editCategoryName}
                        onChange={handleEditNameChange}
                        onBlur={() => {
                          setTouched({ ...touched, editName: true });
                          setErrors({ ...errors, editName: validateInput(editCategoryName, 'Category name') });
                        }}
                        maxLength={50}
                      />
                      {touched.editName && errors.editName && (
                        <div className="invalid-feedback d-block">
                          {errors.editName}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label>
                        Code<strong>*</strong>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${touched.editCode && errors.editCode ? 'is-invalid' : ''}`}
                        placeholder="Enter Code (e.g. CAT001)"
                        value={editCategoryCode}
                        onChange={handleEditCodeChange}
                        onBlur={() => {
                          setTouched({ ...touched, editCode: true });
                          setErrors({ ...errors, editCode: validateInput(editCategoryCode, 'Category code', true) });
                        }}
                        maxLength={10}
                      />
                      {touched.editCode && errors.editCode && (
                        <div className="invalid-feedback d-block">
                          {errors.editCode}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {/* <div className="button-settings">
                <div className="col-sm-12">
                  <div className="d-flex justify-content-end gap-3">
                    <button
                      className="reset"
                      onClick={() => {
                        setEditCategoryName(editingProductCategory.name);
                        setEditCategoryCode(editingProductCategory.code);
                        setErrors({ ...errors, editName: '', editCode: '' });
                        setTouched({ ...touched, editName: false, editCode: false });
                      }}
                    >
                      Reset
                    </button>
                    <button
                      className="save"
                      onClick={handleEditCategory}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      )}

      {/* Category List */}
      {methodType === '' && (
        <div className="row">
          <div className="col-sm-12">
            <div className="setting-right setting-right-table">
              <div className="table-header">
                <div className="search-card">
                  <img src={window.getPublicUrl('/assets/images/search-icon.svg')} alt="Search" />
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search Product category..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                </div>
                <div className="table-actions">
                {checkAddPermission(data?.permissions, 'category') && (
                  <button className="btn" onClick={() => handleImportClickFile(setShowImportModal, setImportFile, setImportErrors)}>
                    <img src={window.getPublicUrl('/assets/images/import.png')} alt="Import" />
                    Import
                  </button>
                )}
                  <button className="btn" onClick={() => handleExportFile(productCategory, searchQuery, 'category.xlsx', '/product-category/export')}>
                    <img src={window.getPublicUrl('/assets/images/export.svg')} alt="Export" />
                    Export
                  </button>
                  {checkAddPermission(data?.permissions, 'category') && (
                    <button className="btn add" onClick={() => setMethodType('add')}>
                      + Add New Category
                    </button>
                  )}
                </div>
              </div>



              <div className="permission-table-inner  category-table">
                <table className="custom-table table-striped">
                  <thead>
                    <tr>
                      <th className="checkbox">
                        <label className="checbox-cs">
                          <input type="checkbox" onClick={(e) => handleUpdateAllCheckbox(e?.target?.checked, setProductCategory)} />
                          <span className="checkmark"></span>
                        </label>
                      </th>
                      <th className="code">Code</th>
                      <th className="name">Name</th>
                      <th className="action">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan="4" className="text-center">
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </td>
                      </tr>
                    ) : error ? (
                      <tr>
                        <td colSpan="4" className="text-center text-danger">{error}</td>
                      </tr>
                    ) : productCategory.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center">No category found</td>
                      </tr>
                    ) : (
                      productCategory.map((method, index) => (
                        <tr key={method.id}>
                          <td className="checkbox">
                            <label className="checbox-cs">
                              <input type="checkbox" onClick={(e) => handleUpdateDataCheckbox(method, e, setProductCategory)} checked={method?.status} />
                              <span className="checkmark"></span>
                            </label>
                          </td>
                          <td className="code">{method.code}</td>
                          <td className="name">{method.name}</td>
                          <td className="action action-cs">
                            <div className="dropdown">
                            {/* <button class=" dropdown-toggle action-btn" type="button" id="dropdownMenuButton1" data-bs-toggle="dropdown" aria-expanded="false">
                                Action <img src={window.getPublicUrl('/assets/images/action-drops.png')} alt="Actions" className="ps-2" />
                              </button> */}
                                {(checkEditPermission(data?.permissions, 'category') || checkDeletePermission(data?.permissions, 'category')) && (
                                <button class=" dropdown-toggle action-btn" type="button" id="dropdownMenuButton1" data-bs-toggle="dropdown" aria-expanded="false"> <img src={window.getPublicUrl('/assets/images/dots-icon.svg')} alt="" /></button>
                                )}
                              <ul class="dropdown-menu" aria-labelledby="dropdownMenuButton1">
                                <li>
                                {checkEditPermission(data?.permissions, 'category') && (
                                
                                    <a href="#" className='dropdown-item' onClick={() => openEditForm(method)}>Edit</a>
                                  
                                )}
                                </li>
                                <li>
                                {checkDeletePermission(data?.permissions, 'category') && (
                                    <a href="#" className='dropdown-item' onClick={() => {
                                    setCategoryToDelete(method);
                                    setShowDeleteModal(true);
                                  }}>Delete</a>
                                )}
                                </li>
                               </ul>
                         
                              
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

            
              </div>
              <div className="table-footer d-flex justify-content-between table-pagination">
                  <select
                    name=""
                    id=""
                    className="affter-change-cs page"
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1); // Reset to first page when changing items per page
                    }}
                  >
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="250">250</option>
                    <option value="500">500</option>
                  </select>
                  <div>
                    <TableFooter
                      pageCount={pageCount}
                      handlePageClick={handlePageClick}
                      currentPage={currentPage}
                    />
                  </div>
                </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Category;